/**
 * Turns raw device-sync failures into messages a porteiro can actually read.
 *
 * The LAN/direct sync path (deviceCommunication.ts + the vendor adapters) surfaces the
 * device's own response verbatim — for Hikvision that is a JSON body like
 * `{"statusCode":6,"subStatusCode":"deviceUserAlreadyExistFace","errorMsg":"PicFeaturePoints"}`
 * wrapped in two layers of "HTTP 400: Bad Request - ". Dumping that into a toast is noise:
 * the operator cannot act on it and the real cause is buried.
 *
 * This is the browser-side twin of `MapHikvisionUploadError` in
 * iot-connect/internal/service/photo_service.go — the cloud path already translates these
 * codes, the direct path did not. Keep the two tables in sync when adding a code.
 */

/** Longest message we are willing to put in a toast before truncating. */
const MAX_LENGTH = 160;

/**
 * A classified failure: what the operator reads, plus the short code they quote.
 *
 * The code exists because the message cannot be quoted reliably — it is prose and it is
 * translated. Two reports of the same failure have to be recognisable as one, and the raw
 * vendor string (the thing we deliberately stopped showing) is what used to serve that
 * purpose.
 */
export interface DeviceSyncErrorClassification {
  code: string;
  /** Short label — a toast title has to fit one line, so it is never a sentence. */
  title: string;
  /** The full, actionable explanation. */
  message: string;
}

/**
 * Portuguese description for a Hikvision subStatusCode / errorMsg pair.
 * Returns null when the code is unknown to us.
 */
function mapHikvisionSubStatus(subStatus: string, errorMsg: string): DeviceSyncErrorClassification | null {
  switch (subStatus) {
    case 'SubpicAnalysisModelingError':
      if (errorMsg === 'PicFeaturePoints') {
        return { code: 'FACE-01', title: 'Rosto não detectado', message: 'Não foi possível detectar os pontos faciais. Use uma foto frontal nítida.' };
      }
      if (errorMsg === 'PicQuality') {
        return { code: 'FACE-02', title: 'Qualidade da foto baixa', message: 'Qualidade da foto insuficiente para reconhecimento facial. Use uma foto mais nítida e bem iluminada.' };
      }
      return { code: 'FACE-03', title: 'Falha ao processar a foto', message: 'Falha no processamento facial da foto. Verifique se a foto mostra claramente o rosto da pessoa.' };
    case 'deviceUserAlreadyExistFace':
    case 'FaceExistAlready':
    case 'FaceIDAlreadyExist':
      return { code: 'FACE-04', title: 'Rosto já cadastrado', message: 'Este rosto já está cadastrado no dispositivo. Revogue o acesso e sincronize novamente para substituir a foto.' };
    case 'FaceQualityNotSatisfied':
      return { code: 'FACE-05', title: 'Foto recusada', message: 'Qualidade da foto não atende aos requisitos do dispositivo. Use uma foto frontal de boa qualidade.' };
    case 'FaceDatabaseFull':
      return { code: 'FACE-06', title: 'Limite de rostos atingido', message: 'Limite de rostos cadastrados no dispositivo atingido. Remova usuários não utilizados para liberar espaço.' };
    case 'FaceNumberExceed':
      return { code: 'FACE-07', title: 'Limite de rostos por pessoa', message: 'Número máximo de rostos por pessoa atingido no dispositivo.' };
    case 'FaceNotExist':
      return { code: 'FACE-08', title: 'Rosto não encontrado', message: 'Registro facial não encontrado no dispositivo.' };
    case 'IsSameWithCurrentFace':
      return { code: 'FACE-09', title: 'Foto idêntica à atual', message: 'A foto enviada é idêntica à já registrada no dispositivo.' };
    case 'PicSizeNotSatisfied':
      return { code: 'FACE-10', title: 'Dimensões da foto inválidas', message: 'Dimensões da foto fora do intervalo aceito pelo dispositivo.' };
    case 'notSupport':
      return { code: 'DEV-01', title: 'Operação não suportada', message: 'Operação não suportada por este dispositivo.' };
    default:
      return subStatus ? { code: 'DEV-02', title: 'Dispositivo recusou a operação', message: `O dispositivo recusou a operação (${subStatus}).` } : null;
  }
}

/** Extracts the vendor JSON body embedded in an error string, if there is one. */
function extractVendorBody(raw: string): { subStatusCode?: string; errorMsg?: string } | null {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) return null;
  try {
    const parsed = JSON.parse(raw.slice(start, end + 1));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Internal sentinel the Electron sync path uses for a device the desktop app could not
 * reach on the LAN. It is a developer phrase ("Não alcançado localmente") that leaked
 * straight into the operator's list, so it is translated here like any vendor code —
 * producers keep writing the sentinel, the UI shows the sentence.
 */
export const LOCAL_UNREACHABLE = 'Não alcançado localmente';

/** Transport-level failures, matched before we look for a vendor body. */
function mapTransportError(raw: string): DeviceSyncErrorClassification | null {
  const lower = raw.toLowerCase();

  if (lower === LOCAL_UNREACHABLE.toLowerCase()) {
    return { code: 'LAN-01', title: 'Dispositivo fora da rede local', message: 'Dispositivo não encontrado na rede local. Verifique se ele está ligado e na mesma rede do computador.' };
  }
  // Already user-facing (services/deviceCommunication.ts) — it still earns a code, because a
  // message is not something anyone can quote back to support.
  if (/comunicação direta falhou/.test(lower)) {
    return { code: 'LAN-02', title: 'Dispositivo inacessível', message: raw.trim() };
  }
  if (/etimedout|timed? ?out|timeout/.test(lower)) {
    return { code: 'NET-01', title: 'Dispositivo não respondeu', message: 'O dispositivo não respondeu a tempo. Verifique se ele está ligado e na rede.' };
  }
  if (/econnrefused|ehostunreach|enetunreach|econnreset|enotfound|network error|failed to fetch/.test(lower)) {
    return { code: 'NET-02', title: 'Falha de conexão', message: 'Não foi possível conectar ao dispositivo. Verifique o IP e a conexão de rede.' };
  }
  // A 401 with no WWW-Authenticate header at all (electron/deviceHttp.ts). Digest IS what
  // these devices use and what we send — this is the device refusing the connection without
  // naming a method, which Hikvision units do when they run out of simultaneous sessions.
  // That is why a long sync can push 100 people through and then fail the next 15.
  if (/did not offer an authentication method|authentication required but not digest/.test(lower)) {
    return { code: 'AUTH-01', title: 'Conexão recusada', message: 'Excesso de conexões simultâneas no dispositivo. Tente novamente; se persistir, revise usuário e senha.' };
  }
  if (/\b401\b|unauthorized|authentication failed|digest auth/.test(lower)) {
    return { code: 'AUTH-02', title: 'Usuário ou senha inválidos', message: 'Usuário ou senha do dispositivo inválidos. Revise as credenciais cadastradas.' };
  }
  if (/\b403\b|forbidden/.test(lower)) {
    return { code: 'AUTH-03', title: 'Acesso negado (403)', message: 'O dispositivo recusou o acesso (403). Verifique as permissões do usuário configurado.' };
  }
  return null;
}

/**
 * Face-upload failures that carry no vendor JSON body — the ControlID path's own prose.
 *
 * `device rejected the photo — user <id>: <reason>` comes from deviceCommunication.ts
 * (uploadControlIDFacePhoto / the batch photo endpoint). It used to reach the operator
 * verbatim, in English, with the device's internal numeric user id attached. The id is
 * noise to a porteiro; the support code is what identifies the failure.
 *
 * Checked AFTER mapTransportError on purpose: a photo that timed out on the way to the
 * device was never *refused*, and "envie outra foto" would send the operator down the
 * wrong path when the fix is to retry.
 */
function mapFaceUploadError(raw: string): DeviceSyncErrorClassification | null {
  const lower = raw.toLowerCase();

  if (/device rejected the photo|dispositivo recusou a foto/.test(lower)) {
    return {
      code: 'FACE-11',
      title: 'Foto recusada',
      message: 'Envie outra foto frontal, nítida e bem iluminada.',
    };
  }
  if (/face upload failed|the device still lists no face/.test(lower)) {
    return {
      code: 'FACE-12',
      title: 'Falha ao enviar a foto',
      message: 'Não foi possível enviar a foto ao dispositivo. Tente sincronizar novamente.',
    };
  }
  return null;
}

/** Collapses whitespace and cuts the message down to toast size. */
function tidy(message: string): string {
  const flat = message.replace(/\s+/g, ' ').trim();
  return flat.length > MAX_LENGTH ? `${flat.slice(0, MAX_LENGTH - 1)}…` : flat;
}

/**
 * Converts a raw sync error into the message shown to the operator plus its support code.
 * Unknown errors are tidied and truncated rather than passed through verbatim.
 */
export function classifyDeviceSyncError(raw?: string | null): DeviceSyncErrorClassification {
  const text = (raw ?? '').trim();
  if (!text) return { code: 'GEN-00', title: 'Falha na sincronização', message: 'Falha na sincronização' };

  // "user updated/synced but ..." means the person record landed on the device and only
  // the face upload failed — a materially different outcome, worth keeping in the message.
  //
  // Both verbs are real: deviceCommunication.ts writes "user synced but …" on the
  // Hikvision direct path (~line 904) and on the ControlID path (~line 2092), while the
  // older Hikvision wording is "user updated but …". Matching only "updated" let every
  // ControlID photo failure fall through to the verbatim-English fallback below.
  const faceOnly = /^user (updated|synced|created) but/i.test(text);
  const withNuance = ({ code, title, message }: DeviceSyncErrorClassification) => ({
    code,
    // A photo-only failure is NOT a failed sync — saying "Rosto não detectado" alone would
    // have the operator re-syncing a person who is already on the device.
    // The nuance lives in the TITLE alone. It used to ALSO prepend "Cadastro atualizado,
    // mas a foto não foi aceita: " to the message — 47 characters restating the title
    // inside a two-line clamp, which pushed the only actionable sentence out of view:
    //   "Cadastro ok, foto recusada / Cadastro atualizado, mas a foto não foi aceita: o
    //    dispositivo recusou a foto. Envie outra foto…"
    // Said three times, instructed zero times. Title = what happened, message = what to do.
    title: faceOnly ? 'Cadastro ok, foto recusada' : title,
    message: tidy(message),
  });

  const body = extractVendorBody(text);
  if (body) {
    const mapped = mapHikvisionSubStatus(body.subStatusCode ?? '', body.errorMsg ?? '');
    if (mapped) return withNuance(mapped);
  }

  // Transport failures carry the same "only the photo failed" nuance as vendor codes: the
  // person IS on the device, so the message must not read as a total failure.
  const transport = mapTransportError(text);
  if (transport) return withNuance(transport);

  // Vendor prose about the photo itself, with no JSON body to parse.
  const faceUpload = mapFaceUploadError(text);
  if (faceUpload) return withNuance(faceUpload);

  // Unrecognised: strip any JSON blob so braces never reach the UI, then truncate.
  // The second pass handles an UNTERMINATED blob — a truncated device response leaves
  // a dangling "{" that the balanced pattern cannot match.
  const stripped = text
    .replace(/\{[\s\S]*\}/g, '')
    .replace(/\{[\s\S]*$/, '')
    .replace(/\s*-\s*$/, '')
    .replace(/\s*:\s*$/, '')
    .trim();
  return { code: 'GEN-01', title: 'Falha na sincronização', message: tidy(stripped || 'Falha na sincronização') };
}

/** The message alone — what the operator reads. */
export function humanizeDeviceSyncError(raw?: string | null): string {
  return classifyDeviceSyncError(raw).message;
}

/** The code alone — what they quote to support, and what makes two reports one. */
export function deviceSyncErrorCode(raw?: string | null): string {
  return classifyDeviceSyncError(raw).code;
}

/** The short label alone — what a toast puts on its title line. */
export function deviceSyncErrorTitle(raw?: string | null): string {
  return classifyDeviceSyncError(raw).title;
}

export interface DeviceFailure {
  device_id: string;
  device_name: string;
  error: string;
}

export interface DeviceFailureGroup {
  /** Humanised message shared by every device in the group. */
  message: string;
  deviceNames: string[];
  /**
   * Ready-to-use single-line toast text, "<devices>: <cause>".
   *
   * Kept for non-visual callers (logs, tests). The TOAST no longer uses it: as one run-on string
   * it wrapped into a four-line paragraph where the device names came first and the actual failure
   * arrived last, so the operator read two lines of hardware before learning what went wrong.
   * `DeviceFailureToast` renders [message] and [deviceList] as two lines instead.
   */
  toastText: string;
  /** The same device names as [toastText] uses, without the cause. */
  deviceList: string;
  /** Short support code for the cause — see DeviceSyncErrorClassification. */
  code: string;
  /** Short label for the toast's title line; [message] is the explanation below it. */
  title: string;
}

/** How many device names to spell out before summarising the rest as a count. */
const MAX_NAMES_IN_TOAST = 3;

/**
 * "A, B" / "A, B, C e mais 2 dispositivos" — the device list as a person reads it.
 *
 * Exported so the toast component can render the same elision rule it has always used, instead of
 * a second copy that drifts.
 */
export function formatDeviceList(names: string[]): string {
  if (names.length <= MAX_NAMES_IN_TOAST) return names.join(', ');
  const shown = names.slice(0, MAX_NAMES_IN_TOAST).join(', ');
  const rest = names.length - MAX_NAMES_IN_TOAST;
  return `${shown} e mais ${rest} dispositivo${rest > 1 ? 's' : ''}`;
}

/**
 * Groups per-device failures by their humanised cause, so four devices rejecting the same
 * photo produce ONE toast instead of four identical walls of JSON.
 */
export function groupDeviceFailures(failures: DeviceFailure[]): DeviceFailureGroup[] {
  // Grouped by MESSAGE, not by code: two causes can share a code family but read
  // differently, and the operator groups by what they read.
  const byMessage = new Map<string, { names: string[]; code: string; title: string }>();

  for (const failure of failures) {
    const { code, title, message } = classifyDeviceSyncError(failure.error);
    const group = byMessage.get(message);
    if (group) {
      group.names.push(failure.device_name);
    } else {
      byMessage.set(message, { names: [failure.device_name], code, title });
    }
  }

  return Array.from(byMessage.entries()).map(([message, { names, code, title }]) => ({
    message,
    code,
    title,
    deviceNames: names,
    toastText: `${formatDeviceList(names)}: ${message}`,
    deviceList: formatDeviceList(names),
  }));
}

/**
 * One-line summary of a successful sync. Spelling out every device name is unreadable past
 * a handful of devices, so long lists collapse to a count.
 *
 * `totalAttempted` is what makes a PARTIAL sync legible: "4 dispositivos sincronizados"
 * sitting next to a failure toast naming 8 other devices reads as a contradiction, because
 * neither number carries the denominator. The two sets are disjoint — each device lands in
 * exactly one of them — so the success line must say "4 de 12" whenever some devices failed.
 */
export function formatSyncSuccess(
  deviceNames: string[],
  modeBreakdown: string[],
  totalAttempted?: number,
): string {
  const suffix = modeBreakdown.length > 0 ? ` (${modeBreakdown.join(', ')})` : '';
  if (deviceNames.length === 0) return `Sincronização concluída${suffix}`;

  // Partial: the denominator matters more than the individual names.
  if (totalAttempted !== undefined && totalAttempted > deviceNames.length) {
    return `${deviceNames.length} de ${totalAttempted} dispositivos sincronizados${suffix}`;
  }

  if (deviceNames.length <= 2) {
    return `${deviceNames.join(', ')} sincronizado(s)${suffix}`;
  }
  return `${deviceNames.length} dispositivos sincronizados${suffix}`;
}
